import type React from "react"
import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom"

// Authentication & RBAC
import { AuthProvider } from "@/features/auth/stores/AuthContext"
import { AdminRoute, PublicRoute } from "@/features/auth/components/RouteProtection"

// Client Feature Imports
import { Header, Footer } from "@/features/client/layouts/client-layout"
import {
  Home,
  Destination,
  Visa,
  About,
  Contact,
  FlyAndShare,
  TermsOfService,
  PrivacyPolicy,
  NotFound404,
} from "@/features/client/pages/client-pages"

// Admin Feature Imports
import AdminLayout from "@/features/admin/layouts/AdminLayout"
import {
  LazyDashboard,
  LazyPackages,
  LazyVisaApplications,
  LazyTravelInsurance,
  LazyTestimonials,
  LazyAdminLogin,
} from "@/features/admin/components/LazyPages"
import CompanyInfo from "@/features/admin/pages/CompanyInfo"
import Account from "@/features/admin/pages/Account"
import Settings from "@/features/admin/pages/Settings"
import Notifications from "@/features/admin/pages/Notifications"
import { Messages } from "@/features/admin/pages/admin-pages"
import DeletedMessages from "@/features/admin/pages/Messages/DeletedMessages"

// Page Transition Components
import { PageTransition } from "@/features/admin/components/PageTransition"

// Shared Components
import { BackToTop, ScrollToTop } from "@/shared/components/shared-components"
import ErrorBoundary from "@/shared/components/ErrorBoundary"
import { ToastProvider } from "@/shared/components/Toast"
import { LegalConsentManager } from "@/shared/components/LegalConsentManager"




// Component to conditionally render legal consent only on client side
function ClientLegalConsentManager() {
  const location = useLocation()
  
  // Only show legal consent popup on client routes (not admin routes)
  const isAdminRoute = location.pathname.startsWith('/admin')
  
  if (isAdminRoute) {
    console.log('Legal consent popup disabled on admin route:', location.pathname)
    return null
  }
  
  console.log('Legal consent popup enabled on client route:', location.pathname)
  return <LegalConsentManager />
}

function MainContent() {
  return (
    <div className="min-h-screen flex scroll-smooth">
      <div className="flex-1 flex flex-col">
        <Header />

        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/destination" element={<Destination />} />
            <Route path="/visa" element={<Visa />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/fly-and-share" element={<FlyAndShare />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            <Route path="*" element={<NotFound404 />} />
          </Routes>
        </main>
        <Footer />
        <BackToTop />
      </div>
    </div>
  )
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AuthProvider>
            <ScrollToTop />
            <ClientLegalConsentManager />
            <Routes>
              {/* Admin Login (public) */}
              <Route
                path="/admin/login"
                element={
                  <PublicRoute>
                    <PageTransition><LazyAdminLogin /></PageTransition>
                  </PublicRoute>
                }
              />

              {/* Protected Admin Routes */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<PageTransition><LazyDashboard /></PageTransition>} />
                <Route path="packages">
                  <Route index element={<PageTransition><LazyPackages /></PageTransition>} />
                  <Route path="archived" element={<PageTransition><LazyPackages /></PageTransition>} />
                  <Route path="expired" element={<PageTransition><LazyPackages /></PageTransition>} />
                  <Route path="deleted" element={<PageTransition><LazyPackages /></PageTransition>} />
                </Route>
                <Route path="testimonials" element={<PageTransition><LazyTestimonials /></PageTransition>} />
                <Route path="inbox">
                  <Route path="messages" element={<PageTransition><Messages /></PageTransition>} />
                  <Route path="messages/deleted" element={<PageTransition><DeletedMessages /></PageTransition>} />
                  <Route path="deleted-messages" element={<PageTransition><DeletedMessages /></PageTransition>} />
                  <Route path="visa-applications" element={<PageTransition><LazyVisaApplications /></PageTransition>} />
                  <Route path="travel-insurance" element={<PageTransition><LazyTravelInsurance /></PageTransition>} />
                </Route>
                <Route path="company-info" element={<PageTransition><CompanyInfo /></PageTransition>} />
                {/** Legal Documents admin disabled for static public pages **/}
                <Route path="settings" element={<PageTransition><Settings /></PageTransition>} />
                <Route path="account" element={<PageTransition><Account /></PageTransition>} />
                <Route path="notifications" element={<PageTransition><Notifications /></PageTransition>} />
              </Route>

              {/* Public Routes */}
              <Route path="/*" element={<MainContent />} />
            </Routes>
          </AuthProvider>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  )
}

export default App
